import { z } from 'zod';
import { SiteDate, SiteWeather } from './site';

/** Автопогода для отчёта смены (ПР-02): Open-Meteo по адресу проекта. */
export const SiteProjectWeatherQuery = z.strictObject({
  date: SiteDate,
});
export type SiteProjectWeatherQuery = z.infer<typeof SiteProjectWeatherQuery>;

export const SiteProjectWeather = z.strictObject({
  projectId: z.uuid(),
  date: SiteDate,
  weather: SiteWeather,
  /** Откуда взяты координаты */
  locationLabel: z.string().min(1).max(200),
  source: z.literal('open-meteo'),
});
export type SiteProjectWeather = z.infer<typeof SiteProjectWeather>;

/** Порог мороза для бетонирования, °C (ПР-16): при температуре не выше — предупреждение */
export const SITE_CONCRETE_FROST_C = 5;
/** Порог сильного ветра для работ на высоте и свайных, м/с (ПР-16) */
export const SITE_STRONG_WIND_MS = 10;

export const SITE_DAY_PLAN_WEATHER_WARNING_KINDS = ['concrete_frost', 'height_wind', 'pile_wind'] as const;
export type SiteDayPlanWeatherWarningKind = (typeof SITE_DAY_PLAN_WEATHER_WARNING_KINDS)[number];

export const SiteDayPlanWeatherWarning = z.strictObject({
  kind: z.enum(SITE_DAY_PLAN_WEATHER_WARNING_KINDS),
  message: z.string().min(1).max(300),
  /** Сколько работ плана попало под предупреждение */
  activityCount: z.number().int().min(1),
});
export type SiteDayPlanWeatherWarning = z.infer<typeof SiteDayPlanWeatherWarning>;

/** Погода в плане дня (ПР-16): мягкий отказ — weather null, warnings пустые */
export const SiteDayPlanWeather = z.strictObject({
  weather: SiteWeather.nullable(),
  locationLabel: z.string().min(1).max(200).nullable(),
  source: z.enum(['open-meteo', 'unavailable']),
  warnings: z.array(SiteDayPlanWeatherWarning),
});
export type SiteDayPlanWeather = z.infer<typeof SiteDayPlanWeather>;

export type DayPlanActivityKind = 'concrete' | 'height' | 'pile';

const CONCRETE_RE = /бетон/i;
const HEIGHT_RE = /высот|кровл|фасад|лесам/i;
const PILE_RE = /свай|шпунт/i;

/** Виды работ по названию позиции графика — для предупреждений ПР-16. */
export function dayPlanActivityKinds(name: string): DayPlanActivityKind[] {
  const out: DayPlanActivityKind[] = [];
  if (CONCRETE_RE.test(name)) out.push('concrete');
  if (HEIGHT_RE.test(name)) out.push('height');
  if (PILE_RE.test(name)) out.push('pile');
  return out;
}

function worksWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'работа';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'работы';
  return 'работ';
}

/** Предупреждения плана дня по погоде и названиям работ. */
export function buildDayPlanWeatherWarnings(
  weather: SiteWeather | null | undefined,
  activityNames: string[],
): SiteDayPlanWeatherWarning[] {
  if (!weather) return [];
  let concrete = 0;
  let height = 0;
  let pile = 0;
  for (const name of activityNames) {
    const kinds = dayPlanActivityKinds(name);
    if (kinds.includes('concrete')) concrete += 1;
    if (kinds.includes('height')) height += 1;
    if (kinds.includes('pile')) pile += 1;
  }
  const warnings: SiteDayPlanWeatherWarning[] = [];
  const t = weather.temperatureC;
  if (concrete > 0 && t !== null && t <= SITE_CONCRETE_FROST_C) {
    warnings.push({
      kind: 'concrete_frost',
      message: `Бетонирование при ${t} °C: риск мороза (порог ${SITE_CONCRETE_FROST_C} °C) — ${concrete} ${worksWord(concrete)}`,
      activityCount: concrete,
    });
  }
  const wind = weather.windMs;
  if (height > 0 && wind !== null && wind >= SITE_STRONG_WIND_MS) {
    warnings.push({
      kind: 'height_wind',
      message: `Работы на высоте при ветре ${wind} м/с (порог ${SITE_STRONG_WIND_MS} м/с) — ${height} ${worksWord(height)}`,
      activityCount: height,
    });
  }
  if (pile > 0 && wind !== null && wind >= SITE_STRONG_WIND_MS) {
    warnings.push({
      kind: 'pile_wind',
      message: `Свайные работы при ветре ${wind} м/с (порог ${SITE_STRONG_WIND_MS} м/с) — ${pile} ${worksWord(pile)}`,
      activityCount: pile,
    });
  }
  return warnings;
}
